import {
  Bell, AlertTriangle, Calendar, PoundSterling,
  FileText, ClipboardCheck, UserCheck, MessageCircle, Wrench, Mail,
  CreditCard, Clock, RefreshCw
} from 'lucide-react'
import type { NotificationAlertType, ReferenceType } from '../types/notifications'
import type { UserRole } from '../types/user'

// ============================================================
// CONFIG: Icon & colour maps for every notification type
// ============================================================

export const NOTIFICATION_ICONS: Record<NotificationAlertType, typeof Bell> = {
  compliance_expiring: Calendar,
  compliance_expired: AlertTriangle,
  rent_due: PoundSterling,
  rent_overdue: AlertTriangle,
  issue_created: FileText,
  issue_updated: FileText,
  contractor_assigned: Wrench,
  quote_submitted: PoundSterling,
  quote_approved: ClipboardCheck,
  job_completed: ClipboardCheck,
  comment_added: MessageCircle,
  document_served: FileText,
  document_acknowledged: FileText,
  onboarding_step_completed: ClipboardCheck,
  onboarding_complete: UserCheck,
  viewing_scheduled: Calendar,
  viewing_confirmed: UserCheck,
  viewing_declined: AlertTriangle,
  prospect_declined: FileText,
  prospect_invite: Mail,
  system: Bell,
  work_started: Wrench,
  issue_closed: ClipboardCheck,
  quote_rejected: AlertTriangle,
  quote_withdrawn: AlertTriangle,
  quote_declined: AlertTriangle,
  quote_edited: FileText,
  photos_uploaded: FileText,
  invoice_submitted: PoundSterling,
  invoice_paid: PoundSterling,
  tenant_satisfied: UserCheck,
  tenant_concern: AlertTriangle,
  contractor_unassigned: Wrench,
  prospect_application: FileText,
  prospect_document_uploaded: FileText,
  referencing_started: ClipboardCheck,
  referencing_complete: ClipboardCheck,
  prospect_converted: UserCheck,
  contractor_invited: Mail,
  contractor_removed: AlertTriangle,
  tenant_invite: Mail,
  subscription_started: CreditCard,
  subscription_cancelled: CreditCard,
  payment_succeeded: CreditCard,
  payment_failed: AlertTriangle,
  spend_approval_requested: PoundSterling,
  spend_approval_decided: ClipboardCheck,
  landlord_client_invited: Mail,
  statement_available: FileText,
  compliance_expiring_client: Calendar,
  new_issue_client: FileText,
  onboarding_reminder: ClipboardCheck,
  expense_reminder: RefreshCw,
  trial_expiry_warning: Clock,
  trial_expired: AlertTriangle,
  scheduled_maintenance_due: Calendar,
  scheduled_maintenance_overdue: AlertTriangle,
  scheduled_maintenance_created: Wrench,
  scheduled_maintenance_completed: ClipboardCheck,
  written_statement_missing: AlertTriangle,
  government_info_sheet_deadline: Calendar,
  rent_increase_notice: PoundSterling,
  tenancy_message_received: MessageCircle,
  visit_proposed: Calendar,
  visit_accepted: UserCheck,
  visit_declined: AlertTriangle,
  visit_alternative_proposed: RefreshCw,
  visit_cancelled: AlertTriangle,
  visit_reminder: Clock,
}

export const NOTIFICATION_COLORS: Record<NotificationAlertType, string> = {
  compliance_expiring: 'text-amber-500 bg-amber-50',
  compliance_expired: 'text-red-500 bg-red-50',
  rent_due: 'text-amber-500 bg-amber-50',
  rent_overdue: 'text-red-500 bg-red-50',
  issue_created: 'text-blue-500 bg-blue-50',
  issue_updated: 'text-blue-500 bg-blue-50',
  contractor_assigned: 'text-blue-500 bg-blue-50',
  quote_submitted: 'text-amber-500 bg-amber-50',
  quote_approved: 'text-emerald-500 bg-emerald-50',
  job_completed: 'text-emerald-500 bg-emerald-50',
  comment_added: 'text-blue-500 bg-blue-50',
  document_served: 'text-emerald-500 bg-emerald-50',
  document_acknowledged: 'text-emerald-500 bg-emerald-50',
  onboarding_step_completed: 'text-amber-500 bg-amber-50',
  onboarding_complete: 'text-emerald-500 bg-emerald-50',
  viewing_scheduled: 'text-blue-500 bg-blue-50',
  viewing_confirmed: 'text-emerald-500 bg-emerald-50',
  viewing_declined: 'text-red-500 bg-red-50',
  prospect_declined: 'text-zinc-500 bg-zinc-50',
  prospect_invite: 'text-blue-500 bg-blue-50',
  system: 'text-zinc-500 bg-zinc-50',
  work_started: 'text-blue-500 bg-blue-50',
  issue_closed: 'text-emerald-500 bg-emerald-50',
  quote_rejected: 'text-red-500 bg-red-50',
  quote_withdrawn: 'text-zinc-500 bg-zinc-50',
  quote_declined: 'text-red-500 bg-red-50',
  quote_edited: 'text-amber-500 bg-amber-50',
  photos_uploaded: 'text-blue-500 bg-blue-50',
  invoice_submitted: 'text-amber-500 bg-amber-50',
  invoice_paid: 'text-emerald-500 bg-emerald-50',
  tenant_satisfied: 'text-emerald-500 bg-emerald-50',
  tenant_concern: 'text-red-500 bg-red-50',
  contractor_unassigned: 'text-zinc-500 bg-zinc-50',
  prospect_application: 'text-blue-500 bg-blue-50',
  prospect_document_uploaded: 'text-blue-500 bg-blue-50',
  referencing_started: 'text-blue-500 bg-blue-50',
  referencing_complete: 'text-emerald-500 bg-emerald-50',
  prospect_converted: 'text-emerald-500 bg-emerald-50',
  contractor_invited: 'text-blue-500 bg-blue-50',
  contractor_removed: 'text-zinc-500 bg-zinc-50',
  tenant_invite: 'text-blue-500 bg-blue-50',
  subscription_started: 'text-emerald-500 bg-emerald-50',
  subscription_cancelled: 'text-zinc-500 bg-zinc-50',
  payment_succeeded: 'text-emerald-500 bg-emerald-50',
  payment_failed: 'text-red-500 bg-red-50',
  spend_approval_requested: 'text-amber-500 bg-amber-50',
  spend_approval_decided: 'text-emerald-500 bg-emerald-50',
  landlord_client_invited: 'text-blue-500 bg-blue-50',
  statement_available: 'text-emerald-500 bg-emerald-50',
  compliance_expiring_client: 'text-amber-500 bg-amber-50',
  new_issue_client: 'text-blue-500 bg-blue-50',
  onboarding_reminder: 'text-amber-500 bg-amber-50',
  expense_reminder: 'text-[#c87941] bg-orange-50',
  trial_expiry_warning: 'text-amber-500 bg-amber-50',
  trial_expired: 'text-red-500 bg-red-50',
  scheduled_maintenance_due: 'text-amber-500 bg-amber-50',
  scheduled_maintenance_overdue: 'text-red-500 bg-red-50',
  scheduled_maintenance_created: 'text-blue-500 bg-blue-50',
  scheduled_maintenance_completed: 'text-emerald-500 bg-emerald-50',
  written_statement_missing: 'text-red-500 bg-red-50',
  government_info_sheet_deadline: 'text-amber-500 bg-amber-50',
  rent_increase_notice: 'text-amber-500 bg-amber-50',
  tenancy_message_received: 'text-blue-500 bg-blue-50',
  visit_proposed: 'text-blue-500 bg-blue-50',
  visit_accepted: 'text-emerald-500 bg-emerald-50',
  visit_declined: 'text-red-500 bg-red-50',
  visit_alternative_proposed: 'text-amber-500 bg-amber-50',
  visit_cancelled: 'text-red-500 bg-red-50',
  visit_reminder: 'text-amber-500 bg-amber-50',
}

// ============================================================
// Click-to-navigate: maps (referenceType, role) → URL
// ============================================================

export function getNotificationUrl(
  referenceType: ReferenceType | null,
  referenceId: string | null,
  role: UserRole | undefined
): string | null {
  if (!referenceType || !referenceId) return null

  switch (referenceType) {
    case 'issue':
      if (role === 'contractor') return `/jobs/${referenceId}`
      if (role === 'tenant') return `/my-issues/${referenceId}`
      if (role === 'landlord_client') return `/client/issues`
      return `/maintenance/${referenceId}`

    case 'compliance_document':
      if (role === 'landlord_client') return `/client/compliance`
      return role === 'landlord' ? `/compliance/${referenceId}` : null

    case 'rent_payment':
      if (role === 'landlord_client') return `/client/financials`
      return role === 'landlord' ? `/financials` : null

    case 'tenant_document':
      if (role === 'tenant') return `/portal/documents`
      return `/tenants`

    case 'tenancy':
      if (role === 'tenant') return `/portal/tenancy`
      return `/tenants`

    case 'viewing':
      if (role === 'landlord') return `/viewings`
      return null

    case 'prospect':
      if (role === 'landlord') return `/prospects/${referenceId}`
      return null

    case 'financial_transaction':
      if (role === 'landlord_client') return `/client/financials`
      return role === 'landlord' ? `/financials` : null

    case 'spend_approval':
      // The client is told a request was raised; the agent who raised it is
      // now told how it was decided (spend_approval_decided, migration 400),
      // so the agent side needs a destination too. It had `null` because,
      // until N4, no notification of this family ever reached an agent.
      if (role === 'landlord_client') return `/client/approvals`
      return `/maintenance`

    case 'application':
      if (role === 'landlord') return `/prospects/${referenceId}`
      return null

    case 'referencing':
      if (role === 'landlord') return `/prospects/${referenceId}`
      return null

    case 'tenant_invite':
      if (role === 'tenant') return `/portal`
      return `/tenants`

    case 'scheduled_maintenance':
      return `/maintenance?tab=scheduled`

    case 'tenancy_message':
      if (role === 'tenant') return `/portal/messages`
      return `/tenants`

    default:
      return null
  }
}
